// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Icon,
    Translate,
    Tooltip,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import {
    failoverDomains,
    IAvailableIpBlock,
} from 'common/api/resources/FailoverDomain';
import {
    ContentBody,
    SectionHeader,
    TableRow,
    TableCell,
    TableHeaderRow,
    WarningBanner,
} from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/Styles';
import { PopoverList } from 'common/components/PopoverList/PopoverList';
import {
    FAILOVER_DOMAIN,
    FAILOVER_DOMAIN_ROW,
} from 'admin/failoverDomain/constants/tests';
import { dataCySelector } from 'common/tests/selectors';
import { ICONS } from 'common/constants';

export interface IIpBlockStepProps {
    computeResourceIds: number[];
    failoverDomainId?: number;
    selectedIds: number[];
    searchQuery: string;
    onSelectionChange: (ids: number[]) => void;
}

const isBlockLocked = (block: IAvailableIpBlock): boolean =>
    block.is_attached && !block.can_detach;

export const IpBlockStep: React.FC<IIpBlockStepProps> = ({
    computeResourceIds,
    failoverDomainId,
    selectedIds,
    searchQuery,
    onSelectionChange,
}) => {
    const [ipBlocks, setIpBlocks] = React.useState<IAvailableIpBlock[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const isInitializedRef = React.useRef(false);

    const loadData = React.useCallback(async (crIds: number[], query: string) => {
        if (crIds.length === 0) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await failoverDomains.availableIpBlocks({
                compute_resource_ids: crIds,
                failover_domain_id: failoverDomainId,
                filters: query ? { search: query } : undefined,
            });

            const data = response.data.data;
            setIpBlocks(data);

            if (!isInitializedRef.current) {
                const preselected = data
                    .filter(block => block.is_attached)
                    .map(block => block.id);
                onSelectionChange(preselected);
                isInitializedRef.current = true;
            }
        } catch {
            // Error is handled by API layer
        } finally {
            setIsLoading(false);
        }
    }, [failoverDomainId, onSelectionChange]);

    React.useEffect(() => {
        loadData(computeResourceIds, searchQuery);
    }, [computeResourceIds, searchQuery, loadData]);

    const handleAdd = (block: IAvailableIpBlock) => {
        if (!selectedIds.includes(block.id)) {
            onSelectionChange([...selectedIds, block.id]);
        }
    };

    const handleRemove = (block: IAvailableIpBlock) => {
        if (isBlockLocked(block)) {
            return;
        }
        onSelectionChange(selectedIds.filter(id => id !== block.id));
    };

    const selectedBlocks = ipBlocks.filter(block => selectedIds.includes(block.id));
    const notSelectedBlocks = ipBlocks.filter(block => !selectedIds.includes(block.id));

    const renderFdNames = (block: IAvailableIpBlock) => {
        if (block.failover_domains.length === 0) {
            return <span>-</span>;
        }

        return (
            <PopoverList
                items={block.failover_domains}
                render={(fd) => fd.name}
                keyExtractor={(fd) => String(fd.id)}
            />
        );
    };

    const renderActionButton = (block: IAvailableIpBlock, isSelected: boolean) => {
        if (!isSelected) {
            return (
                <Icon
                    name={ICONS.PLUS}
                    onClick={() => handleAdd(block)}
                    style={{ cursor: 'pointer' }}
                    data-cy={dataCySelector(block.id, FAILOVER_DOMAIN_ROW.IP_ADD)}
                />
            );
        }

        if (isBlockLocked(block)) {
            return (
                <Tooltip title={
                    <Translate
                        content="failoverDomain.create.ip.vmInUseTooltip"
                        params={{ count: block.vm_count }}
                    />
                }>
                    <Icon
                        name={ICONS.MINUS}
                        style={{ cursor: 'not-allowed', opacity: 0.4 }}
                        data-cy={dataCySelector(block.id, FAILOVER_DOMAIN_ROW.IP_REMOVE)}
                    />
                </Tooltip>
            );
        }

        return (
            <Icon
                name={ICONS.MINUS}
                onClick={() => handleRemove(block)}
                style={{ cursor: 'pointer' }}
                data-cy={dataCySelector(block.id, FAILOVER_DOMAIN_ROW.IP_REMOVE)}
            />
        );
    };

    const renderHeaderRow = () => (
        <TableHeaderRow>
            <TableCell width="40px" />
            <TableCell width="60px"><Translate content="failoverDomain.create.ip.id" /></TableCell>
            <TableCell flex={2}><Translate content="failoverDomain.create.ip.name" /></TableCell>
            <TableCell width="60px"><Translate content="failoverDomain.create.ip.type" /></TableCell>
            <TableCell width="80px"><Translate content="failoverDomain.create.ip.listType" /></TableCell>
            <TableCell width="80px"><Translate content="failoverDomain.create.ip.totalIps" /></TableCell>
            <TableCell width="80px"><Translate content="failoverDomain.create.ip.reservedIps" /></TableCell>
            <TableCell flex={1}><Translate content="failoverDomain.create.ip.usedForFd" /></TableCell>
        </TableHeaderRow>
    );

    const renderTableRow = (block: IAvailableIpBlock, isSelected: boolean) => {
        const actionButton = renderActionButton(block, isSelected);

        return (
            <TableRow key={block.id} data-cy={dataCySelector(block.id, FAILOVER_DOMAIN_ROW.IP_ROW)}>
                <TableCell width="40px">
                    {actionButton}
                </TableCell>
                <TableCell width="60px">
                    {block.id}
                </TableCell>
                <TableCell flex={2}>
                    {block.name}
                </TableCell>
                <TableCell width="60px">
                    {block.type}
                </TableCell>
                <TableCell width="80px">
                    {block.list_type}
                </TableCell>
                <TableCell width="80px">
                    {block.total_ips}
                </TableCell>
                <TableCell width="80px">
                    {block.reserved_ips_count}
                </TableCell>
                <TableCell flex={1}>
                    {renderFdNames(block)}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <ContentBody data-cy={FAILOVER_DOMAIN.IP_STEP}>
            <WarningBanner>
                <Icon name={ICONS.INFO_CIRCLE} size={16} />
                <Translate content="failoverDomain.create.ip.routingWarning" />
            </WarningBanner>
            <Loader isLoading={isLoading && ipBlocks.length === 0}>
                {ipBlocks.length > 0 && renderHeaderRow()}
                {selectedBlocks.length > 0 && (
                    <>
                        <SectionHeader>
                            <Translate content="failoverDomain.create.ip.selected" />
                            {` (${selectedBlocks.length})`}
                        </SectionHeader>
                        {selectedBlocks.map(block => renderTableRow(block, true))}
                    </>
                )}
                {notSelectedBlocks.length > 0 && (
                    <>
                        <SectionHeader>
                            <Translate content="failoverDomain.create.ip.notSelected" />
                            {` (${notSelectedBlocks.length})`}
                        </SectionHeader>
                        {notSelectedBlocks.map(block => renderTableRow(block, false))}
                    </>
                )}
                {!isLoading && ipBlocks.length === 0 && (
                    <TableRow>
                        <TableCell flex={1}>
                            <Translate content="failoverDomain.create.ip.empty" />
                        </TableCell>
                    </TableRow>
                )}
            </Loader>
        </ContentBody>
    );
};

export default IpBlockStep;
